/**
 * Order Service
 *
 * API client for orders with fallback to mock data.
 * OPERATION IDENTITY - Step 3
 */

import { getIdToken } from '../lib/auth';

import type { Order, OrderSummary } from '../types/order';

// =============================================================================
// Configuration
// =============================================================================

const ORDERS_API_URL = import.meta.env.VITE_ORDERS_API_URL ?? '';

// =============================================================================
// API Helper
// =============================================================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    nextToken?: string;
    count?: number;
  };
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = await getIdToken();

  const response = await fetch(`${ORDERS_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      'x-tenant-id': 'geauxfile',
      ...options.headers,
    },
  });

  const data = await response.json() as ApiResponse<T>;

  if (!response.ok) {
    throw new Error(data.error?.message ?? `API error: ${response.status}`);
  }

  return data;
}

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Get all orders for the current user
 */
export async function getMyOrders(): Promise<OrderSummary[]> {
  if (!ORDERS_API_URL) throw new Error('Orders service unavailable: API not configured');

  const response = await apiRequest<OrderSummary[]>('/orders');
  const orders = response.data ?? [];

  // P1-1 Fix: Client-side deduplication as defense-in-depth
  const seenOrderIds = new Set<string>();
  return orders.filter((order) => {
    if (seenOrderIds.has(order.orderId)) {
      return false;
    }
    seenOrderIds.add(order.orderId);
    return true;
  });
}

/**
 * Normalize a raw order record from the API into the portal Order shape.
 * The payment-consumer writes records with `amount` (cents), `status: 'PAID'` (uppercase),
 * and metadata-based item info — but no `items`, `subtotal`, `tax`, or `total` fields.
 */
function normalizeOrder(raw: Record<string, unknown>): Order {
  const status = (typeof raw.status === 'string' ? raw.status.toLowerCase() : 'pending') as Order['status'];

  // Handle amount (cents) vs total (dollars)
  const rawTotal = (raw.total as number) ?? (raw.amount as number) ?? 0;
  const total = rawTotal > 1000 ? rawTotal / 100 : rawTotal; // Convert cents if > $10 threshold

  // Build items array from metadata if items[] is missing
  let items = raw.items as Order['items'] | undefined;
  if (!items || !Array.isArray(items) || items.length === 0) {
    const metadata = raw.metadata as Record<string, string> | undefined;
    const itemIds = metadata?.itemIds?.split(',') ?? [];
    const serviceMap: Record<string, string> = {
      'ra-la-annual': 'Louisiana Registered Agent (Annual)',
      'ra-ms-annual': 'Mississippi Registered Agent (Annual)',
      'ra-al-annual': 'Alabama Registered Agent (Annual)',
      'website-basic': 'Basic Business Website',
    };
    items = itemIds.length > 0
      ? itemIds.map((id, index) => ({
          id: `item-${index}`,
          name: serviceMap[id.trim()] ?? 'Registered Agent Service',
          quantity: 1,
          unitPrice: total / itemIds.length,
        }))
      : [{
          id: 'item-0',
          name: 'Registered Agent Service',
          quantity: 1,
          unitPrice: total,
        }];
  }

  return {
    orderId: (raw.orderId as string) ?? '',
    createdAt: (raw.createdAt as string) ?? new Date().toISOString(),
    updatedAt: (raw.updatedAt as string) ?? (raw.createdAt as string) ?? new Date().toISOString(),
    status,
    items,
    subtotal: (raw.subtotal as number) ?? total,
    tax: (raw.tax as number) ?? 0,
    total,
    userId: raw.userId as string | undefined,
    guestEmail: raw.guestEmail as string | undefined,
    paymentMethod: (raw.paymentMethod as string) ?? (raw.paymentMethodId ? 'card' : undefined),
    paymentDate: (raw.paymentDate as string) ?? (raw.paidAt as string),
    invoiceId: raw.invoiceId as string | undefined,
    stripePaymentIntentId: raw.paymentIntentId as string | undefined,
    fulfillmentStatus: (raw.fulfillmentStatus as Order['fulfillmentStatus']) ?? 'pending',
  };
}

/**
 * Get a single order by ID
 */
export async function getOrderById(orderId: string): Promise<Order | null> {
  if (!ORDERS_API_URL) throw new Error('Orders service unavailable: API not configured');

  try {
    const response = await apiRequest<Record<string, unknown>>(`/orders/${orderId}`);
    if (!response.data) return null;
    return normalizeOrder(response.data);
  } catch (error) {
    console.error('Failed to fetch order:', error);
    return null;
  }
}

/**
 * Get order count for dashboard stats
 */
export async function getOrderCount(): Promise<number> {
  if (!ORDERS_API_URL) throw new Error('Orders service unavailable: API not configured');

  const orders = await getMyOrders();
  return orders.length;
}

/**
 * Get orders by status
 */
export async function getOrdersByStatus(status: Order['status']): Promise<OrderSummary[]> {
  if (!ORDERS_API_URL) throw new Error('Orders service unavailable: API not configured');

  // Filter client-side since API doesn't have status filter endpoint yet
  const allOrders = await getMyOrders();
  return allOrders.filter((order) => order.status === status);
}

/**
 * Link guest orders to the current authenticated user
 */
export async function linkGuestOrders(email?: string, orderId?: string): Promise<{
  linkedCount: number;
  linkedOrderIds: string[];
  message: string;
}> {
  if (!ORDERS_API_URL) throw new Error('Orders service unavailable: API not configured');

  const body: Record<string, string> = {};
  if (email) body.email = email;
  if (orderId) body.orderId = orderId;

  const response = await apiRequest<{
    linkedCount: number;
    linkedOrderIds: string[];
    message: string;
  }>('/orders/link-guest', {
    method: 'POST',
    body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
  });

  return response.data ?? { linkedCount: 0, linkedOrderIds: [], message: 'No orders linked' };
}
